/**
 * Settings → Pressure Control → Certificate rules by class (fieldiq-api#1333,
 * WL-0, migration 0610).
 *
 * An optional certificate interval per component class: years in service and
 * total years, each 1 to 20, in service no more than total. A class with no
 * rule uses the company limits in the card above ("Company default"). A rule
 * is the tenant's own default, never a regulation value, and a new
 * certificate snapshots whichever applies when it is recorded, so a change
 * here does not touch a certificate already on file.
 *
 *   GET    /pressure-control/class-rules                  every role that reads pressure control
 *   PUT    /pressure-control/class-rules/:componentClass  admin, office staff
 *   DELETE /pressure-control/class-rules/:componentClass  admin, office staff
 *
 * The years a class uses (`effective`) and which source they come from are the
 * API's; this card renders them and computes nothing. The 1–20 and
 * in-service-≤-total checks below are a HINT shown while typing; Save is never
 * refused for them. The API's 400 is the authority and its sentence is shown.
 */
import React, { useEffect, useState } from 'react';
import { Loader2, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { LoadFailureNotice } from '../ui/LoadFailureNotice';
import { PcErrorLine } from './PcSectionNav';
import { useDeletePcClassRule, usePcClassRules, usePutPcClassRule } from '../../hooks/usePressureControl';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { pcClassLabel, pcErrorMessage } from '../../lib/pressure-control';
import type { PcClassRuleRow, PcClassRuleWrite } from '../../types/pressure-control';
import { DH_INPUT, DH_SECONDARY, DhDialog, DhField, useOpenCount } from '../../pages/downhole/DownholeBits';

export const PC_CLASS_RULE_MIN_YEARS = 1;
export const PC_CLASS_RULE_MAX_YEARS = 20;

export interface ClassRuleDraft {
  inService: string;
  total: string;
  notes: string;
}

const yearsOf = (t: string): number | null => (/^\d+$/.test(t.trim()) ? Number(t.trim()) : null);

/**
 * A hint about the draft, or null. Never used to refuse the save: the API
 * enforces the same rule and its 400 is what the reader is shown.
 */
export function pcClassRuleHint(d: ClassRuleDraft): string | null {
  const a = yearsOf(d.inService);
  const b = yearsOf(d.total);
  const range = (n: number | null) => n !== null && n >= PC_CLASS_RULE_MIN_YEARS && n <= PC_CLASS_RULE_MAX_YEARS;
  if (!range(a) || !range(b)) {
    return `Each figure is a whole number of years from ${PC_CLASS_RULE_MIN_YEARS} to ${PC_CLASS_RULE_MAX_YEARS}.`;
  }
  if ((a as number) > (b as number)) return 'Years in service cannot be more than total years.';
  return null;
}

/**
 * The PUT body. A figure that is not a whole number is sent as null so the
 * API names the field in its refusal; a blank note is sent as null (clears it).
 */
export function pcClassRuleBody(d: ClassRuleDraft): PcClassRuleWrite {
  const note = d.notes.trim();
  return {
    max_in_service_years: yearsOf(d.inService),
    max_total_years: yearsOf(d.total),
    notes: note === '' ? null : note,
  };
}

const draftOf = (row: PcClassRuleRow | null): ClassRuleDraft => ({
  inService: row?.rule ? String(row.rule.maxInServiceYears) : '',
  total: row?.rule ? String(row.rule.maxTotalYears) : '',
  notes: row?.rule?.notes ?? '',
});

/** The words for where a class's limits come from. An unknown source reads `—`. */
export function pcClassRuleSource(row: PcClassRuleRow): string {
  if (row.rule || row.effective?.source === 'class_rule') return 'Set for this class';
  if (row.effective?.source === 'company_settings') return 'Company default';
  return '—';
}

const yearsText = (n: number | null | undefined) => (typeof n === 'number' && Number.isFinite(n) ? String(n) : '—');

function ClassRuleDialog({ row, onClose }: { row: PcClassRuleRow | null; onClose: () => void }) {
  const open = row !== null;
  const put = usePutPcClassRule();
  const openCount = useOpenCount(open);
  const [d, setD] = useState<ClassRuleDraft>(draftOf(null));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setD(draftOf(row));
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, openCount]);

  const hint = pcClassRuleHint(d);
  const label = row ? pcClassLabel(row.componentClass) : '—';

  const submit = async () => {
    if (!row) return;
    setError(null);
    try {
      await put.mutateAsync({ componentClass: String(row.componentClass), body: pcClassRuleBody(d) });
      toast.success(`${label} rule saved`);
      onClose();
    } catch (e) {
      setError(pcErrorMessage(e, 'The rule could not be saved.'));
    }
  };
  const set = (k: keyof ClassRuleDraft) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setD({ ...d, [k]: e.target.value });

  return (
    <DhDialog
      open={open}
      onClose={onClose}
      title={`Certificate rule — ${label}`}
      footer={
        <>
          <button type="button" className={DH_SECONDARY} onClick={onClose}>Cancel</button>
          <button type="button" onClick={submit} disabled={put.isPending} className="fi-action fi-action--standard fi-action--primary">
            {put.isPending && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
            Save rule
          </button>
        </>
      }
    >
      <p className="text-sm text-fieldiq-text">
        Your company&rsquo;s own limit for every {label}, not a regulation. Certificates recorded after you save use it;
        certificates already on file keep their limits.
      </p>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <DhField label="Years in service" hint="From the day it goes into service to its last valid day.">
          <input className={DH_INPUT} inputMode="numeric" value={d.inService} onChange={set('inService')} aria-label="Years in service" />
        </DhField>
        <DhField label="Total years" hint="From the certificate day, for a component with elastomers.">
          <input className={DH_INPUT} inputMode="numeric" value={d.total} onChange={set('total')} aria-label="Total years" />
        </DhField>
        <div className="sm:col-span-2">
          <DhField label="Notes">
            <textarea className={`${DH_INPUT} h-20 py-2`} value={d.notes} onChange={set('notes')} aria-label="Notes" />
          </DhField>
        </div>
      </div>
      {hint && (
        <p className="mt-3 text-sm text-fieldiq-muted" data-testid="pc-class-rule-hint">{hint}</p>
      )}
      <PcErrorLine message={error} />
    </DhDialog>
  );
}

export function PcClassRulesCard({ canWrite }: { canWrite: boolean }) {
  const query = usePcClassRules();
  const remove = useDeletePcClassRule();
  const failure = queryLoadFailure(query);
  const rows = query.data ?? [];
  const [editing, setEditing] = useState<PcClassRuleRow | null>(null);
  const [error, setError] = useState<string | null>(null);

  const onClear = (row: PcClassRuleRow) => {
    setError(null);
    remove.mutate(String(row.componentClass), {
      onSuccess: () => toast.success(`${pcClassLabel(row.componentClass)} now uses the company default`),
      onError: (e) => setError(pcErrorMessage(e, 'The rule could not be cleared.')),
    });
  };

  return (
    <div className="bg-fieldiq-card rounded-xl border border-fieldiq-border p-5 space-y-4" data-testid="pc-class-rules">
      <div>
        <h3 className="text-base font-semibold text-white">Certificate rules by class</h3>
        <p className="mt-1 text-sm text-fieldiq-text">
          Give a component class its own certificate limits. A class with no rule uses the company limits above. These are your
          company&rsquo;s defaults, not a regulation, and a change applies to certificates recorded after it.
        </p>
        {!canWrite && <p className="mt-1 text-sm text-fieldiq-muted">Only an admin or office staff can change these.</p>}
      </div>
      {failure ? (
        <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="certificate rules" />
      ) : query.isLoading ? (
        <Loader2 className="w-5 h-5 text-fieldiq-accent animate-spin" aria-label="Loading certificate rules" />
      ) : rows.length === 0 ? (
        <p className="text-sm text-fieldiq-text">No component classes were returned.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-left">
            <thead className="border-b border-fieldiq-border text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
              <tr>
                <th scope="col" className="px-3 py-2">Class</th>
                <th scope="col" className="px-3 py-2">Limits</th>
                <th scope="col" className="px-3 py-2">Years in service</th>
                <th scope="col" className="px-3 py-2">Total years</th>
                <th scope="col" className="px-3 py-2">Notes</th>
                {canWrite && <th scope="col" className="px-3 py-2" />}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const name = pcClassLabel(row.componentClass);
                return (
                  <tr key={String(row.componentClass)} className="border-t border-fieldiq-border" data-testid="pc-class-rule-row">
                    <td className="px-3 py-2 text-sm font-semibold text-white">{name}</td>
                    <td className="px-3 py-2 text-sm text-fieldiq-text">{pcClassRuleSource(row)}</td>
                    <td className="px-3 py-2 font-mono text-sm tabular-nums text-fieldiq-text">{yearsText(row.effective?.maxInServiceYears)}</td>
                    <td className="px-3 py-2 font-mono text-sm tabular-nums text-fieldiq-text">{yearsText(row.effective?.maxTotalYears)}</td>
                    <td className="px-3 py-2 text-sm text-fieldiq-text">{row.rule?.notes || '—'}</td>
                    {canWrite && (
                      <td className="px-3 py-2">
                        <div className="flex justify-end gap-2">
                          <button type="button" className={DH_SECONDARY} onClick={() => setEditing(row)} aria-label={`${row.rule ? 'Edit' : 'Set'} rule for ${name}`}>
                            <Pencil className="size-4" aria-hidden="true" />
                            {row.rule ? 'Edit' : 'Set'}
                          </button>
                          {row.rule && (
                            <button
                              type="button"
                              className={DH_SECONDARY}
                              onClick={() => onClear(row)}
                              disabled={remove.isPending}
                              aria-label={`Clear rule for ${name}`}
                            >
                              <Trash2 className="size-4" aria-hidden="true" />
                              Clear
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <PcErrorLine message={error} />
      <ClassRuleDialog row={editing} onClose={() => setEditing(null)} />
    </div>
  );
}
